/**
 * @fileoverview Needs Review table displaying unmatched product listings.
 * @module features/sku-alias/components/NeedsReviewTable
 */

import { Check, Info, Loader2, Sparkles } from 'lucide-react'
import { ReactElement, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Money } from '@/shared/components/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { getSourceInfo } from '@/features/sku-alias/utils/sourceContext'
import type {
  ListingId,
  UnmatchedListing,
} from '@/features/sku-alias/schemas/unmatchedListingSchema'
import type { NeedsReviewSortKey } from '@/features/sku-alias/api/needsReview'
import { searchCatalogProducts } from '@/features/sku-alias/api/needsReview'
import { LookupConfirmField } from '@/shared/components/LookupConfirmField'
import type { Variant } from '@/lib/variantService'

interface NeedsReviewTableProps {
  listings: UnmatchedListing[]
  sortKey: NeedsReviewSortKey
  direction: 'asc' | 'desc'
  selectedIds: Set<ListingId>
  onSortChange: (key: NeedsReviewSortKey) => void
  onToggleListing: (listing: UnmatchedListing) => void
  onToggleAll: (listings: UnmatchedListing[], shouldSelect: boolean) => void
  onAssignExisting: (listing: UnmatchedListing, productId: number) => Promise<void> | void
  assigningListingId?: ListingId | null
  isAssigning?: boolean
  /** Handler for generating AI suggestions */
  onGenerateAiSuggestions?: (() => void) | undefined
  /** Loading state for AI suggestion generation */
  isGeneratingAiSuggestions?: boolean | undefined
  /** Handler for clearing/rejecting AI suggestion on a listing */
  onClearSuggestion?: ((listing: UnmatchedListing) => void) | undefined
}



interface SelectionToggleProps {
  checked: boolean
  onToggle: () => void
  ariaLabel: string
  disabled?: boolean
}

const SelectionToggle = ({
  checked,
  onToggle,
  ariaLabel,
  disabled = false,
}: SelectionToggleProps): ReactElement => (
  <button
    type="button"
    onClick={event => {
      event.stopPropagation()
      if (!disabled) {
        onToggle()
      }
    }}
    className={cn(
      'inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      disabled && 'cursor-not-allowed opacity-60'
    )}
    aria-label={ariaLabel}
    aria-disabled={disabled}
  >
    <span
      role="checkbox"
      aria-checked={checked}
      className={`inline-flex h-4 w-4 items-center justify-center rounded-[3px] border ${
        checked ? 'bg-primary text-primary-foreground border-primary' : 'border-border'
      }`}
      data-state={checked ? 'checked' : 'unchecked'}
    >
      {checked ? <Check className="w-3 h-3" /> : null}
    </span>
  </button>
)

type ColumnKey = NeedsReviewSortKey | 'actions' | 'unattributedRevenue'
type Column = { key: ColumnKey; translationKey: string; sortable?: boolean }

const columns: Column[] = [
  {
    key: 'externalSlug',
    translationKey: 'needsReview.table.columns.externalListing',
    sortable: true,
  },
  {
    key: 'unattributedRevenue',
    translationKey: 'needsReview.table.columns.revenue',
    sortable: false,
  },
  { key: 'actions', translationKey: 'needsReview.table.columns.actions' },
]

const renderFinancialAmount = (
  amount: number,
  currency: string | undefined | null
): ReactElement => {
  if (!amount || Number.isNaN(amount)) {
    return <span className="text-muted-foreground">&nbsp;</span>
  }

  const currencyValue = currency ?? 'EUR'
  return <Money value={amount} currency={currencyValue} minFraction={2} maxFraction={2} />
}

const MIN_SEARCH_LENGTH = 0
const MAX_VISIBLE_RESULTS = 6

interface InlineSkuMatcherProps {
  listing: UnmatchedListing
  onAssignExisting: (listing: UnmatchedListing, productId: number) => Promise<void> | void
  onCreateNew: () => void
  isBusy: boolean
  canResolve: boolean
  canCreate: boolean
  /** Handler for clearing/rejecting AI suggestion */
  onClearSuggestion?: ((listing: UnmatchedListing) => void) | undefined
}

const InlineSkuMatcher = ({
  listing,
  onAssignExisting,
  onCreateNew,
  isBusy,
  canResolve,
  canCreate,
  onClearSuggestion,
}: InlineSkuMatcherProps): ReactElement => {
  const { t } = useTranslation('features-sku-alias')

  const handleConfirm = useCallback(
    async (sku: Variant): Promise<void> => {
      if (isBusy) return
      await onAssignExisting(listing, sku.id)
    },
    [isBusy, listing, onAssignExisting]
  )

  const handleReject = useCallback(() => {
    if (onClearSuggestion) {
      onClearSuggestion(listing)
    }
  }, [listing, onClearSuggestion])

  // Check if this listing has an AI suggestion
  const hasSuggestion = listing.suggestedVariantId !== null && listing.suggestedVariantId !== undefined

  // Create a pre-selected item for AI suggestions so user can immediately confirm
  const initialSelectedItem = useMemo((): Variant | undefined => {
    if (listing.suggestedVariantId && listing.suggestedVariantName) {
      return {
        id: listing.suggestedVariantId,
        name: listing.suggestedVariantName,
        productName: listing.suggestedVariantName,
        variantSkuKey: listing.suggestedVariantSkuKey ?? undefined,
      } as Variant
    }
    return undefined
  }, [listing.suggestedVariantId, listing.suggestedVariantName, listing.suggestedVariantSkuKey])

  // Show sparkles icon when AI suggestion is active
  const addonIcon = hasSuggestion ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Sparkles className="size-4 text-muted-foreground/50" />
      </TooltipTrigger>
      <TooltipContent side="left">
        <p className="text-xs">{t('needsReview.table.ai.suggestedMatch')}</p>
      </TooltipContent>
    </Tooltip>
  ) : undefined

  return (
    <LookupConfirmField<Variant>
      fetchResults={searchCatalogProducts}
      onConfirm={handleConfirm}
      onReject={hasSuggestion && onClearSuggestion ? handleReject : undefined}
      {...(canCreate ? { onCreateNew } : {})}
      createNewLabel={t('needsReview.table.actions.createNew')}
      disabled={!canResolve || isBusy}
      renderSuggestion={({ item }) => (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <span
              className="block max-w-[18rem] truncate font-medium"
              title={item.productName ?? item.name}
            >
              {item.productName ?? item.name}
            </span>
            {item.productName && item.name !== item.productName && (
              <span
                className="block max-w-[18rem] truncate text-xs text-muted-foreground"
                title={item.name}
              >
                {item.name}
              </span>
            )}
            <span
              className="block max-w-[18rem] truncate text-[11px] text-muted-foreground/60 font-mono"
              title={item.sku}
            >
              {item.sku}
            </span>
          </div>
          <div className="text-xs text-muted-foreground text-right min-w-[5rem]">
            {typeof item.reportingCost === 'number' ? (
              <Money
                value={item.reportingCost}
                currency={item.reportingCurrency ?? item.originalCurrency ?? undefined}
                minFraction={2}
                maxFraction={2}
              />
            ) : (
              '—'
            )}
          </div>
        </div>
      )}
      getItemLabel={item => item.productName ?? item.name ?? ''}
      placeholder={t('needsReview.table.actions.assignToExisting')}
      minSearchLength={MIN_SEARCH_LENGTH}
      maxVisibleResults={MAX_VISIBLE_RESULTS}
      confirmAriaLabel={t('needsReview.table.actions.confirmMapping')}
      rejectAriaLabel={t('needsReview.table.actions.rejectSuggestion')}
      confirmIcon={
        isBusy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />
      }
      addonIcon={addonIcon}
      initialSelectedItem={initialSelectedItem}
      clearOnBlur={false}
      queryKeyBase={['variant', 'search']}
      className={cn('w-full max-w-sm text-[12px]', hasSuggestion && 'ring-1 ring-muted-foreground/20 rounded-md')}
      inputClassName="text-[12px] leading-5 placeholder:text-[12px]"
      addonClassName="text-[12px]"
      renderHeader={() => (
        <div className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
          <span className="uppercase tracking-wide">{t('needsReview.table.headers.productVariantSku')}</span>
          <span className="uppercase tracking-wide">{t('needsReview.table.headers.unitCost')}</span>
        </div>
      )}
    />
  )
}

/**
 * Renders the Needs Review table.
 *
 * @component
 */
export function NeedsReviewTable({
  listings,
  sortKey,
  direction,
  selectedIds,
  onSortChange,
  onToggleListing,
  onToggleAll: _onToggleAll,
  onAssignExisting,
  assigningListingId = null,
  isAssigning = false,
  onGenerateAiSuggestions,
  isGeneratingAiSuggestions = false,
  onClearSuggestion,
}: NeedsReviewTableProps): ReactElement {
  const { t } = useTranslation('features-sku-alias')
  const navigate = useNavigate()

  return (
    <div className="max-w-full overflow-x-auto rounded-3xl border border-border/15 bg-card">
      <Table>
        <TableHeader>
          <TableRow className="[&_th]:h-14">
            <TableHead className="w-10" />
            {columns.map(column => {
              const sortable = column.sortable === true && column.key !== 'actions'
              const isActive = sortable && sortKey === column.key

              // Render AI button in the actions column header
              if (column.key === 'actions' && onGenerateAiSuggestions) {
                return (
                  <TableHead
                    key={column.key}
                    className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground text-right w-96"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onGenerateAiSuggestions}
                      disabled={isGeneratingAiSuggestions}
                      className="h-8 px-3 text-xs font-medium"
                    >
                      {isGeneratingAiSuggestions ? (
                        <>
                          <Loader2 className="size-3.5 animate-spin" />
                          {t('needsReview.table.ai.generating')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="size-3.5" />
                          {t('needsReview.table.ai.generateSuggestions')}
                        </>
                      )}
                    </Button>
                  </TableHead>
                )
              }

              return (
                <TableHead
                  key={column.key}
                  className={`whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground ${column.key === 'actions' ? 'text-right' : ''}`}
                  aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSortChange(column.key as NeedsReviewSortKey)}
                      className="inline-flex items-center gap-2"
                    >
                      {t(column.translationKey)}
                      {isActive ? (
                        <span className="text-[10px] text-muted-foreground">
                          {direction === 'asc' ? '↑' : '↓'}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    t(column.translationKey)
                  )}
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {listings.map(listing => {
            const checked = selectedIds.has(listing.id)
            const canResolve = listing.canResolve
            const isRowAssigning = isAssigning && assigningListingId === listing.id
            return (
              <TableRow
                key={listing.id}
                className={cn(
                  'border-border/10 h-[4.5rem]',
                  canResolve ? 'cursor-pointer' : 'cursor-default opacity-80'
                )}
                onClick={() => (canResolve ? onToggleListing(listing) : undefined)}
              >
                <TableCell className="align-middle py-4">
                  <SelectionToggle
                    checked={checked}
                    onToggle={() => onToggleListing(listing)}
                    ariaLabel={t('needsReview.table.selection.toggleRow')}
                    disabled={!canResolve}
                  />
                </TableCell>
                <TableCell className="py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {listing.unattributedQuantity > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {listing.unattributedQuantity}×
                      </span>
                    )}
                    {listing.externalProductName ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1.5 font-medium text-sm text-foreground cursor-default">
                            <span className="truncate max-w-[20rem]">{listing.externalSlug}</span>
                            <Info className="size-3 text-muted-foreground/40 shrink-0" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          hideArrow
                          className="bg-card/95 border border-white/10 shadow-2xl rounded-xl px-4 py-2 text-sm text-foreground/90 max-w-sm text-pretty backdrop-blur"
                        >
                          {listing.externalProductName}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="font-medium text-sm text-foreground truncate max-w-[20rem]">
                        {listing.externalSlug}
                      </span>
                    )}
                    {listing.sources.map(source => (
                      <Badge
                        key={source}
                        variant="secondary"
                        className="inline-flex w-fit items-center gap-1 rounded-full bg-muted/50 text-[11px] text-muted-foreground px-2 py-1 capitalize hover:bg-muted/70"
                      >
                        {getSourceInfo(source).displayName}
                      </Badge>
                    ))}
                    {listing.type === 'store_variant_unresolved' ? (
                      <Badge
                        variant="outline"
                        className="inline-flex w-fit items-center gap-1 rounded-full text-[11px] px-2 py-1"
                      >
                        Store variant
                      </Badge>
                    ) : null}
                    {listing.storeName ? (
                      <Badge
                        variant="secondary"
                        className="inline-flex w-fit items-center gap-1 rounded-full text-[11px] px-2 py-1"
                      >
                        {listing.storeName}
                      </Badge>
                    ) : null}
                    {listing.missingSku ? (
                      <Badge
                        variant="destructive"
                        className="inline-flex w-fit items-center gap-1 rounded-full text-[11px] px-2 py-1"
                      >
                        Missing SKU
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-sm font-semibold text-foreground py-4">
                  {renderFinancialAmount(listing.unattributedRevenue, listing.currencyCode)}
                </TableCell>
                <TableCell className="align-middle" onClick={event => event.stopPropagation()}>
                  <div className="flex justify-end">
                    {canResolve ? (
                      <InlineSkuMatcher
                        listing={listing}
                        onAssignExisting={onAssignExisting}
                        onCreateNew={() => navigate('/dashboard/management/products/needs-review/create', { state: { listing } })}
                        isBusy={isRowAssigning}
                        canResolve={canResolve}
                        canCreate={listing.canCreate}
                        onClearSuggestion={onClearSuggestion}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">Missing SKU</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {listings.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {t('needsReview.table.empty')}
        </div>
      ) : null}
    </div>
  )
}

export default NeedsReviewTable
